import React, { FC, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { AxiosResponse } from 'axios'
import * as yup from 'yup'
import { useOktaAuth } from '@okta/okta-react'
import { api, routes } from 'config'
import {
  constants,
  errorMsg,
  processErrorMsg,
  removeFocusFromField,
  resetAndExpiredPasswordSchema,
  storage,
} from 'utils'
import { BaseLayout, Loader } from 'components'
import { ChangePasswordForm, ChangePasswordConfirmation } from './components'

const ChangePassword: FC = () => {
  const navigate = useNavigate()
  const { authState, oktaAuth } = useOktaAuth()

  const [transaction, setTransaction] = useState<any | {}>({})
  const [serviceError, setServiceError] = useState('')
  const [showLoader, setShowLoader] = useState(false)

  useEffect(() => {
    setShowLoader(true)
    setServiceError('')
    console.log(`Paswordpolicy url: ${api.getPasswordPolicy()}`)
    axios
      .get(api.getPasswordPolicy())
      .then((response) => {
        setTransaction({
          isModuleInitialized: true,
          policy: response.data,
        })
        setShowLoader(false)
      })
      .catch((e) => {
        processErrorMsg(
          e?.response?.data || e,
          'Change Password',
          'Fetching Password Policy from Okta',
        )
      })
  }, [])

  const processSuccess = (response: any) => {
    setShowLoader(false)
    setTransaction({
      ...transaction,
      status: response.status,
    })
  }

  const processError = (
    e: any,
    module: string,
    moduleDescription: string = '',
  ) => {
    setShowLoader(false)
    setServiceError(processErrorMsg(e, module, moduleDescription))
  }

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    const {
      payload: { sub: userId, preferred_username, memberMlsId },
    } = oktaAuth.token.decode(authState.idToken.idToken)

    storage.setUser(preferred_username)

    removeFocusFromField()
    setSubmitting(true)
    setShowLoader(true)
    setServiceError('')

    let changePasswordResponse: AxiosResponse

    try {
      changePasswordResponse = await axios.post(api.changePassword(), {
        userId: userId,
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      })
    } catch (e: any) {
      setShowLoader(false)
      setSubmitting(false)
      if (e?.response?.status === 403) {
        processError(
          e?.response?.data?.subErrors,
          'Change Password',
          'Resetting Okta password',
        )
        return
      }
      processError(e, 'Change Password', 'Resetting Okta password')
      return
    }

    try {
      const response: AxiosResponse = await axios.post(api.savePassword(), {
        userId: memberMlsId,
        newPassword: values.newPassword,
        passwordChangeType: constants.PASSWORD_CHANGE_TYPE.CHANGE_PASSWORD,
      })

      if (response.status === 200) {
        setSubmitting(false)
        processSuccess(changePasswordResponse.data)
      } else {
        throw response
      }
    } catch (e) {
      setSubmitting(false)
      processErrorMsg(e, 'Save Password', 'Saving encrypted password')
      processSuccess(changePasswordResponse.data)
    }
  }

  const handleCancel = (e: any) => {
    e.preventDefault()
    setServiceError('')
    navigate(routes.login)
  }

  let formContent: any

  if (transaction.status === constants.OKTA_API_STATUS.PASSWORD_CHANGED) {
    formContent = <ChangePasswordConfirmation serviceError={serviceError} />
  } else {
    let initialValues: any = {}
    let validationSchema: any = yup.object().shape({})

    if (transaction.isModuleInitialized) {
      initialValues = {
        oldPassword: '',
        newPassword: '',
        repeatPassword: '',
      }

      validationSchema = yup.object().shape({
        oldPassword: yup
          .string()
          .required(errorMsg.requiredField('Old password')),
        ...resetAndExpiredPasswordSchema(
          transaction.policy.complexity.minLength,
        ),
      })
    }

    formContent = (
      <ChangePasswordForm
        isModuleInitialized={Boolean(transaction.isModuleInitialized)}
        passwordPolicy={transaction.policy}
        initialValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        serviceError={serviceError}
      />
    )
  }

  return (
    <BaseLayout>
      <Loader visible={showLoader} />
      {formContent}
    </BaseLayout>
  )
}

export default ChangePassword
